import { OptionsMap } from '@components/main/DeviceManagement/DeviceInformationManager/constants'

// Minimal ISO 3166-1 alpha-2 country list for Stripe address / tax ID fields.
// Stripe tax_id_data types are country-scoped: eu_vat (EU member states),
// gb_vat (GB only), us_ein (US only).
export interface ICountryOption {
  code: string
  label: string
}

export const COUNTRY_CODE_OPTIONS: ICountryOption[] = [
  { code: 'AT', label: 'Austria' },
  { code: 'BE', label: 'Belgium' },
  { code: 'BG', label: 'Bulgaria' },
  { code: 'CY', label: 'Cyprus' },
  { code: 'CZ', label: 'Czech Republic' },
  { code: 'DE', label: 'Germany' },
  { code: 'DK', label: 'Denmark' },
  { code: 'EE', label: 'Estonia' },
  { code: 'ES', label: 'Spain' },
  { code: 'FI', label: 'Finland' },
  { code: 'FR', label: 'France' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'GR', label: 'Greece' },
  { code: 'HR', label: 'Croatia' },
  { code: 'HU', label: 'Hungary' },
  { code: 'IE', label: 'Ireland' },
  { code: 'IT', label: 'Italy' },
  { code: 'LT', label: 'Lithuania' },
  { code: 'LU', label: 'Luxembourg' },
  { code: 'LV', label: 'Latvia' },
  { code: 'MT', label: 'Malta' },
  { code: 'NL', label: 'Netherlands' },
  { code: 'PL', label: 'Poland' },
  { code: 'PT', label: 'Portugal' },
  { code: 'RO', label: 'Romania' },
  { code: 'SE', label: 'Sweden' },
  { code: 'SI', label: 'Slovenia' },
  { code: 'SK', label: 'Slovakia' },
  { code: 'US', label: 'United States' },
]

const EU_VAT_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR',
  'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO',
  'SE', 'SI', 'SK',
])

export type TaxIdType = 'eu_vat' | 'gb_vat' | 'us_ein'

export function taxIdTypesForCountry(countryCode: string | undefined): TaxIdType[] {
  if (!countryCode) return []
  if (countryCode === 'GB') return ['gb_vat']
  if (countryCode === 'US') return ['us_ein']
  if (EU_VAT_COUNTRIES.has(countryCode)) return ['eu_vat']
  return []
}

// Stripe billing currency for a location's country. Callers pass the country as
// stored on the account/location, which is a COUNTRY_OPTIONS display name (the
// keys of OptionsMap) rather than an ISO code, so OptionsMap is the source of
// truth — deriving from it keeps this in step with the country dropdown instead
// of drifting when a country is added. The alpha-2 codes above are accepted too,
// since some records predate the dropdown.
export type BillingCurrency = 'EUR' | 'GBP' | 'USD'

const CURRENCY_BY_ALPHA2: Record<string, BillingCurrency> = {
  GB: 'GBP',
  US: 'USD',
}

export function countryToCurrency(country: string): BillingCurrency {
  const raw = country.trim()
  if (!raw) return 'EUR'

  const fromOptions = OptionsMap[raw]?.currency
  if (fromOptions) return fromOptions as BillingCurrency

  const normalized = raw.toLowerCase()
  const matchedName = Object.keys(OptionsMap).find(
    (name) => name.toLowerCase() === normalized,
  )
  if (matchedName) return OptionsMap[matchedName].currency as BillingCurrency

  const upper = raw.toUpperCase()
  if (CURRENCY_BY_ALPHA2[upper]) return CURRENCY_BY_ALPHA2[upper]
  if (upper === 'UK') return 'GBP'
  if (upper === 'USA' || normalized === 'united states') return 'USD'

  return 'EUR' // Ireland + all other EU countries
}
